import React, { useCallback, useEffect, useMemo, useRef, useState, memo } from 'react';

import { Button, Card } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { css, StyleSheet } from 'aphrodite';
import { faThumbtack, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  blackberry,
  coolGrey,
  grape,
  greyStroke,
  shadowElevated,
  shadowSubtle,
  shadowVeryLight,
  softGreenDark,
  softGreenLight,
  softPurple,
  softPurpleLight,
  white,
} from 'lib/css/colors';

const ZOOM_SIZE = 560;
const ZOOM_OFFSET = 8;

function formatGmv(value) {
  if (value == null) return null;
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return '$0';
  if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `$${(num / 1_000).toFixed(1)}K`;
  return `$${num.toFixed(0)}`;
}

function renderSkuLink(product, kind) {
  const sku = kind === 'vendor' ? product?.vendor_sku : product?.manufacturer_sku;
  const productType = kind === 'vendor' ? 'distributor' : 'manufacturer';
  if (!sku) return '—';
  if (!product?.global_product_id || !product?.id) {
    return sku;
  }
  return (
    <a
      href={`/global-product-detail/${product.global_product_id}/${product.id}/${productType}`}
      target="_blank"
      rel="noopener noreferrer"
      className={css(styles.skuLink)}
    >
      {sku}
    </a>
  );
}

// vendor card → zoom anchored to left of card; mfr card → anchored to right of card
// Overlap onto the card/image is intentional and acceptable.
// Both axes are clamped so the overlay stays within the viewport on all screen sizes.
function getZoomStyle(rect, kind) {
  if (!rect) return {};
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const rawTop = rect.top + rect.height / 2 - ZOOM_SIZE / 2;
  const top = Math.max(8, Math.min(rawTop, vh - ZOOM_SIZE - 8));
  const rawLeft =
    kind === 'vendor' ? rect.right - ZOOM_SIZE + ZOOM_OFFSET : rect.left - ZOOM_OFFSET;
  const left = Math.max(8, Math.min(rawLeft, vw - ZOOM_SIZE - 8));
  return { left, top };
}

// Custom memo comparator: compare by product identity (id) and URL content so
// a parent refetch that returns new object references for the same product does
// not cause an unnecessary re-render or image-state reset.
function arePropsEqual(prev, next) {
  return (
    prev.kind === next.kind &&
    prev.gmv === next.gmv &&
    prev.product?.id === next.product?.id &&
    prev.product?.thumbnail_url === next.product?.thumbnail_url &&
    (prev.product?.image_urls || []).join(',') === (next.product?.image_urls || []).join(',')
  );
}

const BrandSkuMapperProductCard = memo(function BrandSkuMapperProductCard({ product, kind, gmv }) {
  const images = useMemo(() => {
    const urls = [product?.thumbnail_url, ...(product?.image_urls || [])].filter(Boolean);
    return [...new Set(urls)];
  }, [product?.thumbnail_url, product?.image_urls]);

  const [selectedImg, setSelectedImg] = useState(images[0] || null);
  // hoverImg: transient on-hover zoom; pinnedImg: stays visible after click
  const [hoverImg, setHoverImg] = useState(null);
  const [hoverRect, setHoverRect] = useState(null);
  const [pinnedImg, setPinnedImg] = useState(null);
  const [pinnedRect, setPinnedRect] = useState(null);
  // Ref to the pinned source element so scroll can recalculate its position
  const pinnedElemRef = useRef(null);

  // Depend on product?.id rather than the images array reference so a refetch
  // that returns new array objects for the same product does not reset the
  // selected and pinned image state unnecessarily.
  useEffect(() => {
    setSelectedImg(images[0] || null);
    setPinnedImg(null);
    pinnedElemRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);

  // Recalculate pinned overlay position on scroll so it tracks the card
  useEffect(() => {
    if (!pinnedImg || !pinnedElemRef.current) return undefined;
    const onScroll = () => {
      if (pinnedElemRef.current) {
        setPinnedRect(pinnedElemRef.current.getBoundingClientRect());
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [pinnedImg]);

  const activeImg = hoverImg || pinnedImg;
  const activeRect = hoverImg ? hoverRect : pinnedRect;
  const isPinned = !!pinnedImg;

  const handleMouseEnter = useCallback((url, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setHoverImg(url);
    setHoverRect(rect);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHoverImg(null);
    setHoverRect(null);
  }, []);

  const handleClick = useCallback(
    (url, e) => {
      const rect = e.currentTarget.getBoundingClientRect();
      if (pinnedImg === url) {
        setPinnedImg(null);
        setPinnedRect(null);
        pinnedElemRef.current = null;
      } else {
        setPinnedImg(url);
        setPinnedRect(rect);
        pinnedElemRef.current = e.currentTarget;
      }
    },
    [pinnedImg],
  );

  const unpin = useCallback(e => {
    e.stopPropagation();
    setPinnedImg(null);
    setPinnedRect(null);
    pinnedElemRef.current = null;
  }, []);

  const handleImgError = useCallback(e => {
    e.target.onerror = null;
    e.target.style.visibility = 'hidden';
  }, []);

  return (
    <Card className={css(styles.productCard)}>
      <Card.Body>
        <div className={css(styles.cardHeaderRow)}>
          <div className={css(styles.sideLabel)}>
            {kind === 'vendor' ? 'Distributor product' : 'MFR suggested match'}
          </div>
          {kind === 'vendor' && gmv != null && (
            <span className={css(styles.gmvPill)} title="Annualized monetizable GMV">
              <span className={css(styles.gmvPillLabel)}>GMV:</span>{' '}
              <span className={css(styles.gmvPillValue)}>{formatGmv(gmv)}</span>
            </span>
          )}
        </div>

        <div
          className={css(
            styles.productImageWrapper,
            isPinned && pinnedImg === selectedImg && styles.productImageWrapperPinned,
          )}
          onMouseEnter={selectedImg ? e => handleMouseEnter(selectedImg, e) : undefined}
          onMouseLeave={handleMouseLeave}
          onClick={selectedImg ? e => handleClick(selectedImg, e) : undefined}
          role="button"
          tabIndex={0}
          onKeyDown={e => {
            if ((e.key === 'Enter' || e.key === ' ') && selectedImg) {
              handleClick(selectedImg, e);
            }
          }}
          aria-label="Click to pin zoomed image"
        >
          {selectedImg ? (
            <img
              src={selectedImg}
              alt={product?.name || ''}
              className={css(styles.productImage)}
              onError={handleImgError}
            />
          ) : (
            <div className={css(styles.productImagePlaceholder)}>No image</div>
          )}
          {isPinned && pinnedImg === selectedImg && (
            <div className={css(styles.pinnedBadge)}>
              <FontAwesomeIcon icon={faThumbtack} />
            </div>
          )}
        </div>

        {images.length > 1 && (
          <div className={css(styles.carouselRow)}>
            {images.map((url, idx) => (
              <Button
                key={idx}
                variant="link"
                className={css(
                  styles.carouselThumb,
                  url === selectedImg && styles.carouselThumbActive,
                  isPinned && pinnedImg === url && styles.carouselThumbPinned,
                )}
                onClick={e => {
                  setSelectedImg(url);
                  handleClick(url, e);
                }}
                onMouseEnter={e => handleMouseEnter(url, e)}
                onMouseLeave={handleMouseLeave}
                aria-label={`View image ${idx + 1}`}
              >
                <img
                  src={url}
                  alt=""
                  className={css(styles.carouselThumbImg)}
                  onError={handleImgError}
                />
              </Button>
            ))}
          </div>
        )}

        <div className={css(styles.productName)}>{product?.name || '—'}</div>
        <div className={css(styles.mutedText)}>
          {kind === 'vendor'
            ? `${product?.vendor || '—'} · ${product?.brand || '—'}`
            : product?.brand || '—'}
        </div>
        <div className={css(styles.mutedText)}>
          {product?.pack_size ? `${product.pack_size}` : '—'}
        </div>
        <div className={css(styles.mutedText)}>
          {kind === 'vendor' ? 'SKU ' : 'MFR SKU '}
          {renderSkuLink(product, kind)}
        </div>
      </Card.Body>

      {activeImg && activeRect && (
        <div
          className={css(styles.zoomOverlay, isPinned && styles.zoomOverlayPinned)}
          style={getZoomStyle(activeRect, kind)}
        >
          {isPinned && (
            <Button
              variant="link"
              className={css(styles.unpinBtn)}
              onClick={unpin}
              aria-label="Unpin zoomed image"
            >
              <FontAwesomeIcon icon={faTimes} />
            </Button>
          )}
          <img src={activeImg} alt="" className={css(styles.zoomImage)} onError={handleImgError} />
        </div>
      )}
    </Card>
  );
}, arePropsEqual);

BrandSkuMapperProductCard.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    thumbnail_url: PropTypes.string,
    image_urls: PropTypes.arrayOf(PropTypes.string),
    vendor: PropTypes.string,
    brand: PropTypes.string,
    pack_size: PropTypes.string,
    vendor_sku: PropTypes.string,
    manufacturer_sku: PropTypes.string,
    global_product_id: PropTypes.number,
  }),
  kind: PropTypes.string.isRequired,
  gmv: PropTypes.number,
};

const styles = StyleSheet.create({
  productCard: {
    borderRadius: 12,
    boxShadow: `0 1px 3px ${shadowVeryLight}`,
    height: '100%',
    position: 'relative',
  },
  cardHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    marginBottom: 12,
  },
  sideLabel: {
    color: coolGrey,
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    fontWeight: 700,
    marginBottom: 12,
  },
  gmvPill: {
    display: 'inline-flex',
    alignItems: 'baseline',
    gap: 6,
    padding: '6px 14px',
    borderRadius: 999,
    backgroundColor: softGreenLight,
    color: softGreenDark,
    border: `1px solid ${softGreenDark}`,
    lineHeight: 1,
  },
  gmvPillLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  gmvPillValue: {
    fontSize: 16,
    fontWeight: 800,
    letterSpacing: 0.2,
  },
  productImageWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 260,
    height: 260,
    margin: '0 auto 10px',
    borderRadius: 8,
    overflow: 'hidden',
    cursor: 'zoom-in',
    border: `2px solid transparent`,
    transition: 'border-color 0.15s',
    ':hover': {
      borderColor: grape,
    },
  },
  productImageWrapperPinned: {
    borderColor: softPurple,
    cursor: 'zoom-out',
  },
  pinnedBadge: {
    position: 'absolute',
    top: 6,
    right: 6,
    backgroundColor: softPurple,
    color: white,
    borderRadius: '50%',
    width: 22,
    height: 22,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 10,
  },
  productImage: {
    maxWidth: '100%',
    maxHeight: '100%',
    width: 'auto',
    height: 'auto',
    objectFit: 'contain',
  },
  productImagePlaceholder: {
    width: '100%',
    height: '100%',
    borderRadius: 8,
    backgroundColor: softPurpleLight,
    color: coolGrey,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
  },
  carouselRow: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 6,
    marginBottom: 12,
  },
  carouselThumb: {
    width: 44,
    height: 44,
    borderRadius: 6,
    border: `2px solid transparent`,
    padding: 0,
    background: 'none',
    cursor: 'zoom-in',
    overflow: 'hidden',
    flexShrink: 0,
    ':hover': {
      borderColor: grape,
    },
  },
  carouselThumbActive: {
    borderColor: grape,
    boxShadow: `0 0 0 2px ${softPurpleLight}`,
  },
  carouselThumbPinned: {
    borderColor: softPurple,
    boxShadow: `0 0 0 2px ${softPurpleLight}`,
  },
  carouselThumbImg: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    display: 'block',
  },
  productName: {
    color: blackberry,
    fontWeight: 700,
    fontSize: 16,
    marginBottom: 4,
  },
  mutedText: {
    color: coolGrey,
    fontSize: 12,
  },
  skuLink: {
    color: grape,
    fontWeight: 600,
    textDecoration: 'none',
    ':hover': {
      color: blackberry,
      textDecoration: 'underline',
    },
  },
  altCard: {
    border: `1px solid ${greyStroke}`,
    borderRadius: 8,
    padding: 10,
    backgroundColor: white,
  },
  zoomOverlay: {
    position: 'fixed',
    zIndex: 9999,
    width: ZOOM_SIZE,
    height: ZOOM_SIZE,
    backgroundColor: white,
    borderRadius: 12,
    boxShadow: `0 8px 32px ${shadowSubtle}`,
    border: `1px solid ${greyStroke}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
    pointerEvents: 'none',
  },
  zoomOverlayPinned: {
    pointerEvents: 'auto',
    border: `2px solid ${softPurple}`,
    boxShadow: `0 8px 40px ${shadowElevated}`,
  },
  unpinBtn: {
    position: 'absolute',
    top: 6,
    right: 6,
    color: coolGrey,
    padding: '2px 6px',
    lineHeight: 1,
    ':hover': {
      color: blackberry,
    },
  },
  zoomImage: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
  },
});

export default BrandSkuMapperProductCard;
